import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { roleHome } from "@/lib/roles";
import { Loader2, Building2, Factory, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DEMO = [
  { label: "Admin", email: "bhagyalaxmi12399@gmail.com", password: "Admin@123" },
  { label: "Enterprise", email: "anil@shaktiprecision.com", password: "Buyer@123" },
  { label: "Buyer", email: "priya@shaktiprecision.com", password: "Buyer@123" },
  { label: "Seller", email: "rajesh@abcmfg.com", password: "Supplier@123" },
];
const SHOW_DEMO = process.env.REACT_APP_SHOW_DEMO_LOGIN === "true";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(roleHome(user.role));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-slate-900 md:block">
        <img src="https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdXBwbHklMjBjaGFpbiUyMHNoaXBwaW5nJTIwY2FyZ298ZW58MHx8fHwxNzg2MzcxMzEyfDA&ixlib=rb-4.1.0&q=85"
          alt="Global logistics" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative flex h-full flex-col justify-between p-10 text-white">
          <Link to="/" className="flex items-center gap-2"><span className="rounded-lg bg-white px-3 py-1.5"><img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-7 w-auto" /></span></Link>
          <div>
            <p className="text-sm font-semibold tracking-wide text-blue-300">Connect. Compare. Close.</p>
            <h2 className="mt-2 font-heading text-4xl font-semibold leading-tight">One platform for the complete procurement lifecycle.</h2>
            <p className="mt-4 max-w-md text-slate-300">Connect with suppliers. Compare offers. Run smarter procurement. Track every PO.</p>
          </div>
          <p className="text-xs text-slate-400">Procurement Management Platform · Demo environment</p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 flex items-center gap-2 md:hidden"><img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-8 w-auto" /></Link>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Welcome to ProcDeal</h1>
          <p className="mt-1 text-sm text-muted-foreground">One platform for your complete procurement lifecycle.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-blue-700 hover:underline">Forgot password?</Link>
              </div>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" />
            </div>
            {error && <p data-testid="login-error" role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
            <p className="text-center text-xs text-muted-foreground">Buyer, Seller, Enterprise and Admin portals — one secure login.</p>
          </form>

          <div className="mt-8">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">New to ProcDeal?</p>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">Choose how you work with ProcDeal</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Link to="/register/enterprise" data-testid="register-enterprise-link"
                className="group rounded-lg border bg-white p-4 transition-all duration-150 hover:-translate-y-[1px] hover:border-slate-400 hover:shadow-sm">
                <Building2 className="h-5 w-5 text-slate-900" />
                <p className="mt-2 font-heading text-sm font-semibold">Enterprise</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Manage your procurement teams, suppliers, sourcing events and subscriptions.</p>
                <p className="mt-2 flex items-center text-xs font-semibold text-slate-900">Register as Enterprise <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" /></p>
              </Link>
              <Link to="/register/seller" data-testid="register-seller-link"
                className="group rounded-lg border bg-white p-4 transition-all duration-150 hover:-translate-y-[1px] hover:border-slate-400 hover:shadow-sm">
                <Factory className="h-5 w-5 text-slate-900" />
                <p className="mt-2 font-heading text-sm font-semibold">Seller</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Manage enquiries, quotations, purchase orders and delivery updates from your buyer customers.</p>
                <p className="mt-2 flex items-center text-xs font-semibold text-slate-900">Register as Seller <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" /></p>
              </Link>
            </div>
          </div>

          {SHOW_DEMO && (
          <div className="mt-8">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Demo accounts — click to fill</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button key={d.label} type="button" data-testid={`demo-fill-${d.label.toLowerCase()}`}
                  onClick={() => { setEmail(d.email); setPassword(d.password); }}
                  className="rounded-md border bg-white px-2 py-2 text-left transition-all duration-150 hover:-translate-y-[1px] hover:shadow-sm">
                  <p className="text-xs font-semibold">{d.label}</p>
                  <p className="truncate text-[10px] text-muted-foreground">{d.email}</p>
                </button>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
